import classNames from 'classnames';
import clsx from 'clsx';
import React, { FC, useEffect, useState } from 'react';
import { TbChevronDown, TbMapPin } from 'react-icons/tb';
import OutsideClickHandler from 'react-outside-click-handler';
import { useNavigate, useParams } from 'react-router-dom';

import { useFetchRestaurantQuery, useFetchRestaurantsByOwnerMutation } from '@/api/hooks/useRestaurant';
import { SimpleRestaurantResponse } from '@/api/lib/restaurant';
import { useAuthContext } from '@/contexts/auth';

import { RestaurantStatus } from '../RestaurantStatus';
import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';

export const RestaurantSelector: FC = () => {
  const navigate = useNavigate();
  const { id: restaurantIdFromUrl } = useParams();
  const [restaurantsData, setRestaurantsData] = useState<SimpleRestaurantResponse[]>([]);
  const { user } = useAuthContext();

  const { id } = useParams();
  const restaurantId = id || '';
  const { data: restaurant } = useFetchRestaurantQuery({ id: restaurantId, withMenu: false }, { enabled: !!restaurantId });

  const [selectedRestaurant, setSelectedRestaurant] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { isLoading, mutate: fetchRestaurantsByOwner } = useFetchRestaurantsByOwnerMutation({
    onSuccess: (data) => {
      setRestaurantsData(data.length > 0 ? data : []);
    }
  });

  const { data: restaurantFromSlug, isLoading: isLoadingRestaurantFromSlug } = useFetchRestaurantQuery(
    { id: restaurantIdFromUrl || '' },
    { enabled: !!restaurantIdFromUrl }
  );

  useEffect(() => {
    fetchRestaurantsByOwner({ search: '' });
  }, [fetchRestaurantsByOwner]);

  useEffect(() => {
    if (restaurantFromSlug && restaurantFromSlug.name) {
      setSelectedRestaurant(restaurantFromSlug.name);
    }
  }, [restaurantFromSlug]);

  const handleSelectRestaurantSuggestion = (id: string, name: string) => {
    setSelectedRestaurant(name);
    setIsOpen(false);
    navigate(`/restaurant/${id}`);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleButtonClick = (status: string, id: string, name: string) => {
    if (status === 'ACCEPTED') {
      handleSelectRestaurantSuggestion(id, name);
    } else if (status === 'PENDING') {
      navigate(`/restaurant/inactive?${name}`);
    } else if (status === 'REJECTED') {
      navigate(`/proposal-restaurant/${id}`);
    }
  };

  return (
    <>
      {user && user.isSupport && (
        <div className="bg-black px-3 py-1 rounded-md w-full">
          <Typography color="white" weight="semibold" variant="h5">
            Propriétaire :
          </Typography>
          {restaurant ? (
            <>
              <Typography color="white" variant="extraSmall">
                {restaurant?.emailOwner}
              </Typography>
              <Typography color="white" transform="capitalize" variant="extraSmall">
                {restaurant?.lastNameOwner} {restaurant?.firstNameOwner}
              </Typography>
            </>
          ) : (
            <Typography color="white" align="center">
              ...
            </Typography>
          )}
        </div>
      )}

      <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}>
        <div className="relative w-full">
          <button
            className={clsx(
              isOpen ? 'rounded-t-md' : 'rounded-md',
              'flex items-center w-full justify-between bg-white border border-gray-300 p-3 cursor-pointer'
            )}
            onClick={toggleDropdown}
          >
            {selectedRestaurant && <RestaurantStatus status={restaurant?.validationStatus} circleOnly />}
            {selectedRestaurant ? (
              <span className="items-center flex-col flex">
                <Typography variant="paragraph">{selectedRestaurant}</Typography>
                <span className="flex gap-1 mt-0.5 items-center">
                  <TbMapPin />
                  <Typography variant="extraSmall" color="gray">
                    {restaurant?.cityName}
                  </Typography>
                </span>
              </span>
            ) : (
              <Typography variant="paragraph" align="left">
                Sélectionnez un restaurant
              </Typography>
            )}
            <TbChevronDown className={`text-xl transition-transform ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
          </button>

          {isOpen && (
            <div className="absolute bg-white overflow-y-scroll overflow-x-hidden w-full z-50 border-x border-b border-gray-300 rounded-b-xl max-h-60">
              {isLoading || isLoadingRestaurantFromSlug ? (
                <div className="flex justify-center py-3">
                  <Spinner />
                </div>
              ) : restaurantsData.length > 0 ? (
                restaurantsData.map((restaurant) => (
                  <div key={restaurant.id}>
                    <button
                      className={classNames(
                        restaurant.validationStatus === 'PENDING' && 'bg-gray-100',
                        'hover:bg-shades-quaternary-hover flex items-center gap-3 hover:cursor-pointer w-full py-3 px-5 text-left'
                      )}
                      onClick={() => handleButtonClick(restaurant.validationStatus, restaurant.id, restaurant.name)}
                    >
                      <RestaurantStatus status={restaurant.validationStatus} circleOnly />
                      <span className="items-start flex-col flex">
                        <Typography variant="paragraph">{restaurant.name}</Typography>
                        {restaurant.cityName && (
                          <span className="flex gap-1 mt-0.5 items-center">
                            <TbMapPin />
                            <Typography variant="extraSmall" color="gray">
                              {restaurant.cityName}
                            </Typography>
                          </span>
                        )}
                      </span>
                    </button>
                    <hr />
                  </div>
                ))
              ) : (
                <div className="py-3 px-5">
                  <Typography variant="paragraph">Aucun restaurant trouvé</Typography>
                </div>
              )}
            </div>
          )}
        </div>
      </OutsideClickHandler>
    </>
  );
};
